import { useRef, useState, useCallback } from "react";
import html2canvas from "html2canvas";
import { BRAND } from "../lib/brand";
import {
  Trophy, X, DownloadSimple, Share, SoccerBall, Basketball, Baseball,
  Star, CheckCircle,
} from "@phosphor-icons/react";

const SPORT_LABEL = { football: "Fútbol", nba: "NBA", mlb: "MLB" };
const SPORT_ICON = { football: SoccerBall, nba: Basketball, mlb: Baseball };

const CONFIDENCE_LABEL = {
  strong: "SÓLIDA",
  good: "BUENA",
  questionable: "DUDOSA",
  weak: "DÉBIL",
};

export default function IGShareModal({ pick, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const generate = useCallback(async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      setImageUrl(canvas.toDataURL("image/png"));
    } catch (e) {
      console.error(e);
    }
    setGenerating(false);
  }, []);

  const download = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `jugada-ganadora-${pick.id || "pick"}.png`;
    a.click();
  };

  const SportIcon = SPORT_ICON[pick.sport] || Trophy;
  const confidenceLabel = pick.scoring ? CONFIDENCE_LABEL[pick.scoring.label] || "SÓLIDA" : "SÓLIDA";
  const confidenceScore = pick.scoring?.confidence || 75;
  const matchDate = pick.match_date
    ? new Date(pick.match_date).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div
      data-testid="ig-share-modal"
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-1">Victoria confirmada</div>
            <h2 className="text-display font-black text-2xl tracking-tight text-white">Compartir jugada ganadora</h2>
          </div>
          <button
            data-testid="ig-share-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/[0.07] hover:bg-white/[0.14] flex items-center justify-center text-zinc-400 hover:text-white transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* The share card (captured by html2canvas) */}
        <div className="mb-5 overflow-hidden rounded-2xl border border-white/[0.08]" style={{ background: "#0A0D14" }}>
          <ShareCard cardRef={cardRef} pick={pick} SportIcon={SportIcon} confidenceLabel={confidenceLabel} confidenceScore={confidenceScore} matchDate={matchDate} />
        </div>

        {/* Preview if generated */}
        {imageUrl && (
          <div className="mb-5 rounded-2xl overflow-hidden border border-emerald-500/20">
            <img src={imageUrl} alt="Preview" className="w-full" data-testid="ig-share-preview" />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          {!imageUrl ? (
            <button
              data-testid="ig-share-generate-btn"
              onClick={generate}
              disabled={generating}
              className="btn-primary flex-1"
            >
              {generating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Star weight="fill" className="w-4 h-4" />
                  Generar imagen
                </span>
              )}
            </button>
          ) : (
            <>
              <button
                data-testid="ig-share-download-btn"
                onClick={download}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <DownloadSimple weight="bold" className="w-4 h-4" />
                Descargar PNG
              </button>
              <button
                onClick={generate}
                className="btn-secondary flex items-center gap-2"
                title="Regenerar"
              >
                <Share className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={onClose} className="btn-secondary px-5">
            Cerrar
          </button>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-3">
          Descarga la imagen y compártela en tus Instagram Stories
        </p>
      </div>
    </div>
  );
}

function ShareCard({ cardRef, pick, SportIcon, confidenceLabel, confidenceScore, matchDate }) {
  return (
    <div
      ref={cardRef}
      style={{
        background: "linear-gradient(145deg, #0D1117 0%, #0A1628 50%, #0D1117 100%)",
        width: "100%",
        padding: "32px 28px",
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background glow */}
      <div style={{
        position: "absolute", top: "-60px", right: "-60px",
        width: "220px", height: "220px",
        background: "radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-40px", left: "-40px",
        width: "180px", height: "180px",
        background: "radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      {/* Top accent line */}
      <div style={{ height: "3px", background: "linear-gradient(90deg, #D4AF37, #10B981, #D4AF37)", borderRadius: "999px", marginBottom: "24px" }} />

      {/* Sport + League badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            width: "28px", height: "28px", borderRadius: "8px",
            background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <SportIcon weight="fill" style={{ width: "14px", height: "14px", color: "#F59E0B" }} />
          </div>
          <span style={{ fontSize: "11px", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "700" }}>
            {pick.league || (pick.sport ? pick.sport.toUpperCase() : "SPORT")}
          </span>
        </div>
        {matchDate && (
          <span style={{ fontSize: "10px", color: "#6B7280", fontFamily: "monospace", letterSpacing: "0.05em" }}>
            {matchDate}
          </span>
        )}
      </div>

      {/* JUGADA GANADORA badge */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: "8px",
        background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)",
        borderRadius: "999px", padding: "6px 14px", marginBottom: "20px",
      }}>
        <Trophy weight="fill" style={{ width: "14px", height: "14px", color: "#10B981" }} />
        <span style={{ fontSize: "12px", color: "#10B981", fontWeight: "800", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          JUGADA GANADORA
        </span>
      </div>

      {/* Match */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "22px", fontWeight: "900", color: "#FFFFFF", lineHeight: "1.1", letterSpacing: "-0.02em" }}>
          {pick.home_team}
        </div>
        <div style={{ fontSize: "10px", color: "#6B7280", fontFamily: "monospace", margin: "4px 0", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          vs
        </div>
        <div style={{ fontSize: "22px", fontWeight: "900", color: "#FFFFFF", lineHeight: "1.1", letterSpacing: "-0.02em" }}>
          {pick.away_team}
        </div>
      </div>

      {/* Pick selection */}
      <div style={{
        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "18px",
      }}>
        <div style={{ fontSize: "10px", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "700", marginBottom: "6px" }}>
          {pick.market || "Mercado"}
        </div>
        <div style={{ fontSize: "18px", fontWeight: "800", color: "#F9FAFB", letterSpacing: "-0.01em" }}>
          {pick.pick_selection}
        </div>
      </div>

      {/* Metrics row — solo confianza y resultado */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
        <div style={{
          flex: 1, background: "rgba(57,255,20,0.07)", border: "1px solid rgba(57,255,20,0.2)",
          borderRadius: "8px", padding: "10px 12px",
        }}>
          <div style={{ fontSize: "9px", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: "700", marginBottom: "3px" }}>
            Confianza
          </div>
          <div style={{ fontSize: "22px", fontWeight: "900", color: "#39FF14", fontFamily: "monospace" }}>
            {confidenceScore}%
          </div>
        </div>
        <div style={{
          flex: 1, background: "rgba(255,215,0,0.07)", border: "1px solid rgba(255,215,0,0.2)",
          borderRadius: "8px", padding: "10px 12px",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircle weight="fill" style={{ width: "22px", height: "22px", color: "#FFD700", marginBottom: "3px" }} />
          <div style={{ fontSize: "9px", color: "#FFD700", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: "800" }}>
            {confidenceLabel}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: "1px", background: "rgba(255,255,255,0.06)", marginBottom: "16px" }} />

      {/* Footer branding — Gana con Ronald */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            background: "linear-gradient(135deg, #1E4D2B, #071A0D)",
            border: "1.5px solid #FFD700",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Trophy weight="fill" style={{ width: "12px", height: "12px", color: "#FFD700" }} />
          </div>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "900", color: "#F9FAFB", letterSpacing: "-0.01em", lineHeight: 1 }}>
              {(() => { const words = BRAND.name.split(" "); const last = words.pop(); return (<>{words.join(" ").toUpperCase()}{words.length > 0 ? " " : ""}<span style={{ color: "#FFD700" }}>{last.toUpperCase()}</span></>); })()}
            </div>
            <div style={{ fontSize: "10px", color: "#39FF14", letterSpacing: "0.05em" }}>@ganaconronald</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "9px", color: "#4B5563", letterSpacing: "0.05em" }}>Tu Mejor Jugada</div>
          <div style={{ fontSize: "9px", color: "#4B5563", letterSpacing: "0.05em" }}>Tu Mejor Ganancia</div>
        </div>
      </div>
    </div>
  );
}
